/**
 * A-25 — how a stored value reads to a person. Pure, so it is tested without a browser.
 *
 * These lived in `Audit.tsx`, which made the screen file export non-components and broke fast
 * refresh. They are also the kind of thing worth testing directly: every case here was on screen
 * in its STORAGE form — a camelCase key upper-cased into one unbroken word, money as the JSON
 * object the services write, a full UUID where a person needed to recognise a piece. None of it
 * was wrong and all of it made the one screen people open when something has gone wrong the
 * hardest to read.
 *
 * Nothing is dropped anywhere here. This is an audit log; the row still holds every stored
 * value in full. It was the presentation that was wrong, not the record.
 */

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * A field name as a person reads it: `returnLinesClosed` → "Return lines closed".
 *
 * The styling upper-cases these, and camelCase upper-cased is one unbroken word —
 * RETURNLINESCLOSED, PIECESINTRANSIT, JOBNUMBERS. The reader was doing the word-splitting.
 */
export function label(k: string): string {
  const words = k
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_.]+/g, ' ')
    .toLowerCase()
    .trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * A value a person reads at a glance.
 *
 * Three shapes were being shown as their storage rather than their meaning:
 *
 *   · **Money** arrived as `{"amount":"1100.0000","currency":"AUD"}`. It is money; it reads as
 *     money. The four stored places become two, as everywhere else (NF-04).
 *   · **UUIDs** filled a column with `d19d7fd8-0851-4a13-8e3b-76773cd89ac1`, which identifies a
 *     row to the database and nothing to a person. Shortened to its first segment, which is
 *     enough to compare two by eye and to paste into the search box.
 *   · **Empty arrays** read as `none` rather than nothing at all, so "no pieces were closed" is
 *     distinguishable from a field that was not recorded.
 *
 * Nothing is dropped. This is an audit log, and a reader who needs the exact stored value has
 * the row; what was wrong was making them read it in storage form.
 */
export function show(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  if (typeof v === 'string') return UUID.test(v) ? v.slice(0, 8) : v;
  if (typeof v === 'number' || typeof v === 'bigint') return v.toString();
  // Recursive rather than `join`: an array of objects would otherwise stringify
  // to a row of `[object Object]`, which is what the linter caught here.
  if (Array.isArray(v)) return v.length ? v.map(show).join(', ') : 'none';

  if (typeof v === 'object') {
    const o = v as Record<string, unknown>;
    // The money shape the services write, in the one place it is read back by a person.
    if (typeof o.amount === 'string' && typeof o.currency === 'string') {
      const n = Number(o.amount);
      return Number.isFinite(n)
        ? `${o.currency} ${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : `${o.currency} ${o.amount}`;
    }
    // A small object reads as its fields; a large one would be a wall either way.
    const keys = Object.keys(o);
    if (keys.length > 0 && keys.length <= 4) {
      return keys.map((k) => `${label(k)}: ${show(o[k])}`).join(' · ');
    }
  }
  return JSON.stringify(v) ?? '—';
}
